/**
 * "Collector splitsen" — pick the kringen that move to a second card.
 *
 * Two people regularly lay part of the same collector, and a collector is sometimes
 * only half finished when the werf moves to a later phase. Both need two cards where
 * there is one, each with its own m², because the onderaannemer PO prices per m².
 *
 * The m² is never typed: it follows each side's share of the total kring length. The
 * preview here is advisory — the API recomputes it and its numbers are the ones that
 * land (see utils/collectorSplit.ts).
 *
 * Deel 2 starts clean: no foto's, niet op druk, geen installateur. That is the normal
 * flow, not a gap — the first photo upload on it sets the foto-status, stamps the
 * druk-datum and claims the card for whoever uploaded.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Collector, Kring } from '@/types/odoo';
import { fetchKringen, splitCollector } from '@/api/tasks';
import {
  previewSplit,
  suggestSplitNames,
  SPLIT_PROBLEM_TEXT,
  type SplitPreviewKring,
} from '@/utils/collectorSplit';

interface SplitKringRow extends SplitPreviewKring {
  x_kringnummer: number;
  x_system: string;
  x_legpatroon: number;
}

export function CollectorSplitModal({
  collector,
  installerNames = {},
  onClose,
  onSplit,
}: {
  collector: Collector;
  /** Odoo partner id → name, for the optional installateur on deel 2. */
  installerNames?: Record<string, string>;
  onClose: () => void;
  /** Called after a successful split so the parent can refresh its lists. */
  onSplit: () => void;
}) {
  const collectorM2 = collector.x_studio_collector_m2 || 0;
  const suggested = useMemo(() => suggestSplitNames(collector.name), [collector.name]);

  const [kringen, setKringen] = useState<SplitKringRow[]>([]);
  const [moved, setMoved] = useState<Set<number>>(new Set());
  const [sourceName, setSourceName] = useState(suggested.sourceName);
  const [newName, setNewName] = useState(suggested.newName);
  const [installerId, setInstallerId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await fetchKringen(collector.id);
        if (cancelled) return;
        setKringen(
          res.kringen.map((k: Kring) => ({
            id: k.id,
            x_length: k.x_length,
            x_kringnummer: k.x_kringnummer,
            x_system: k.x_system || '',
            x_legpatroon: k.x_legpatroon,
          })),
        );
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Kringen laden mislukt');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [collector.id]);

  const preview = useMemo(
    () => previewSplit(kringen, moved, collectorM2),
    [kringen, moved, collectorM2],
  );

  const toggle = (id: number) => {
    setSaveError(null);
    setMoved((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const namesDiffer = sourceName.trim() !== '' && newName.trim() !== '' && sourceName.trim() !== newName.trim();
  const canSubmit = !loading && !saving && preview.problem === null && namesDiffer;

  const handleSplit = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await splitCollector(collector.id, {
        kring_ids: [...moved],
        source_name: sourceName.trim(),
        new_name: newName.trim(),
        installateur_id: installerId ? Number(installerId) : null,
      });
      onSplit();
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Splitsen mislukt');
    } finally {
      setSaving(false);
    }
  };

  const installerEntries = Object.entries(installerNames);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Collector splitsen"
      onClick={onClose}
    >
      <div
        className="mt-10 w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-td-navy">
          &quot;{collector.name}&quot; splitsen
        </h3>
        <p className="mt-1 text-xs text-td-muted">
          Vink de kringen aan die naar deel 2 gaan. De m² wordt verdeeld volgens de
          kringlengtes — {collectorM2} m² in totaal.
        </p>

        {loading ? (
          <p className="py-6 text-sm text-td-muted">Kringen laden…</p>
        ) : loadError ? (
          <p className="py-6 text-sm text-red-600">{loadError}</p>
        ) : (
          <>
            <table className="mt-4 w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wide text-td-muted">
                  <th className="w-24 px-2 py-1">Naar deel 2</th>
                  <th className="w-16 px-2 py-1">#</th>
                  <th className="px-2 py-1">Systeem</th>
                  <th className="w-24 px-2 py-1">Legpatroon</th>
                  <th className="w-24 px-2 py-1">Lengte (m)</th>
                </tr>
              </thead>
              <tbody>
                {kringen.map((k) => (
                  <tr key={k.id} className="border-t border-gray-100">
                    <td className="px-2 py-1">
                      <input
                        type="checkbox"
                        checked={moved.has(k.id)}
                        onChange={() => toggle(k.id)}
                        aria-label={`Kring ${k.x_kringnummer} naar deel 2`}
                        className="h-4 w-4 rounded border-gray-300 text-td-blue focus:ring-td-blue"
                      />
                    </td>
                    <td className="px-2 py-1 font-semibold text-td-navy">{k.x_kringnummer}</td>
                    <td className="px-2 py-1">{k.x_system || '—'}</td>
                    <td className="px-2 py-1">{k.x_legpatroon || '—'}</td>
                    <td className="px-2 py-1">{k.x_length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <SplitColumn
                title="Deel 1 — blijft"
                nameLabel="Naam deel 1"
                name={sourceName}
                onName={setSourceName}
                count={preview.countDeel1}
                length={preview.lengthDeel1}
                m2={preview.m2Deel1}
              />
              <SplitColumn
                title="Deel 2 — nieuw"
                nameLabel="Naam deel 2"
                name={newName}
                onName={setNewName}
                count={preview.countDeel2}
                length={preview.lengthDeel2}
                m2={preview.m2Deel2}
              />
            </div>

            {installerEntries.length > 0 && (
              <label className="mt-3 block text-xs font-semibold text-td-navy">
                Installateur deel 2 (optioneel)
                <select
                  value={installerId}
                  onChange={(e) => setInstallerId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-td-blue"
                >
                  <option value="">-- Nog niet toegewezen --</option>
                  {installerEntries.map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
              </label>
            )}

            <div className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <p>De foto&apos;s blijven bij deel 1.</p>
              <p className="mt-1">
                Deel 2 begint als <strong>nog niet uitgevoerd</strong>: geen foto&apos;s,
                niet op druk. Zodra de installateur er foto&apos;s op zet, komt het
                vanzelf in foto-controle.
              </p>
              <p className="mt-1">
                Is deze werf gemarkeerd als &quot;Collectoren geleverd&quot;? Reken de
                minprijs dan maar één keer aan — die geldt per fysieke collector, niet
                per kaart.
              </p>
            </div>

            {preview.problem && !loading && (
              <p className="mt-3 text-sm text-red-600">{SPLIT_PROBLEM_TEXT[preview.problem]}</p>
            )}
            {!namesDiffer && !preview.problem && (
              <p className="mt-3 text-sm text-red-600">
                Deel 1 en deel 2 moeten een verschillende naam hebben.
              </p>
            )}
            {saveError && (
              <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{saveError}</div>
            )}
          </>
        )}

        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={handleSplit}
            disabled={!canSubmit}
            className="rounded-lg bg-td-blue px-4 py-2 text-sm font-semibold text-white transition hover:bg-td-blue/90 disabled:opacity-50"
          >
            {saving ? 'Splitsen…' : 'Splitsen'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-td-muted transition hover:text-td-navy"
          >
            Annuleren
          </button>
        </div>
      </div>
    </div>
  );
}

function SplitColumn({
  title, nameLabel, name, onName, count, length, m2,
}: {
  title: string;
  nameLabel: string;
  name: string;
  onName: (v: string) => void;
  count: number;
  length: number;
  m2: number | null;
}) {
  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <h4 className="text-xs font-bold text-td-navy">{title}</h4>
      <label className="mt-2 block text-[11px] font-semibold text-td-muted">
        {nameLabel}
        <input
          type="text"
          value={name}
          onChange={(e) => onName(e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-td-blue"
        />
      </label>
      <dl className="mt-2 space-y-0.5 text-xs text-td-muted">
        <div className="flex justify-between"><dt>Kringen</dt><dd className="font-semibold text-td-navy">{count}</dd></div>
        <div className="flex justify-between"><dt>Lengte</dt><dd className="font-semibold text-td-navy">{length} m</dd></div>
        <div className="flex justify-between">
          <dt>m²</dt>
          <dd className="font-bold text-td-navy">{m2 === null ? '—' : `${m2} m²`}</dd>
        </div>
      </dl>
    </div>
  );
}
